import { useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import BigButton from '../components/BigButton'
import BroadcastModal from '../components/BroadcastModal'
import MapPicker from '../components/MapPicker'
import PinGate from '../components/PinGate'
import RegistrationEditRow from '../components/RegistrationEditRow'
import RegistrationsTable from '../components/RegistrationsTable'
import { fetchAdminState, type AdminState } from '../lib/adminState'
import { api, foutTekst } from '../lib/api'
import { nlDatum } from '../lib/format'
import { sortForAssignment } from '../lib/assignQueue'
import type { AdminRegistration, MapMeta, PublicSpot, Season } from '../lib/types'

export default function Beheer() {
  return (
    <PinGate>
      {(token, logout) => <Inner token={token} onLogout={logout} />}
    </PinGate>
  )
}

function Inner({ token, onLogout }: { token: string; onLogout: () => void }) {
  const [registrations, setRegistrations] = useState<AdminRegistration[] | null>(null)
  const [spots, setSpots] = useState<{ meta: MapMeta; spots: PublicSpot[] } | null>(null)
  // Welke editie dit scherm toont. `null` = de lopende editie, waarvan alleen
  // de server het jaartal kent (activeEditionYear() verschuift vanzelf zodra
  // het seizoen heropent) — vandaar een expliciete "huidige" in plaats van
  // hier een jaartal te raden. Een gekozen jaartal zet het scherm in
  // archiefmodus: lezen mag, schrijven niet.
  const [viewEdition, setViewEdition] = useState<number | null>(null)
  const [shownEdition, setShownEdition] = useState<number | null>(null)
  const [isActiveEdition, setIsActiveEdition] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const [onlyUnassigned, setOnlyUnassigned] = useState(false)
  const [onlyWithSpot, setOnlyWithSpot] = useState(false)
  const [paidFilter, setPaidFilter] = useState<'alle' | 'betaald' | 'onbetaald'>('alle')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [editError, setEditError] = useState<string | null>(null)
  const [broadcastRecipients, setBroadcastRecipients] = useState<AdminRegistration[] | null>(null)
  const [toast, setToast] = useState<string | null>(null)
  // De enige /admin/state-lezing van deze pagina. De kaarten hieronder krijgen
  // hem als prop in plaats van er elk zelf een op te halen — zie
  // src/lib/adminState.ts voor waarom alleen doorgeven werkt.
  const [adminState, setAdminState] = useState<AdminState | null>(null)

  // Welke editie-lading de laatste is. Zonder dit schildert een trager
  // antwoord van een editie die je net verlaten hebt alsnog de tabel, de kop
  // én `isActiveEdition` over — in het ergste geval staan de schrijf- en
  // mailknoppen van de lopende editie onder een archiefjaar in de kiezer.
  // Deze requests zijn gepagineerde Notion-lezingen, dus "de organisator
  // klikt sneller dan dat" is de normale situatie, niet de uitzondering.
  const loadSeq = useRef(0)
  // De gekozen editie zoals ze NU is, buiten de sluiting van een render om.
  // Elke mutatiehandler (toewijzen, betaald, bewerken, verwijderen) roept
  // `load()` aan als hij klaar is, en dat is minuten later in
  // Notion-termen: las die het `viewEdition` van zijn eigen render, dan
  // haalde hij de editie op die toen gekozen was, won hij van de lopende
  // archieflading (hij bumpt `loadSeq`) en bleef het scherm voorgoed op
  // "Inschrijvingen van <jaar> laden…" hangen met de rijen van het verkeerde
  // jaar eronder. Niets triggert daarna nog een nieuwe lading, want
  // `viewEdition` verandert niet meer.
  const viewEditionRef = useRef<number | null>(null)

  // Enige plek waar de gekozen editie verandert, zodat ref en state niet uit
  // elkaar kunnen lopen.
  function kiesEditie(next: number | null) {
    // Alles wat aan een specifieke rij hangt hoort bij de editie die je
    // verlaat — laten staan zou de bewerkbalk of een "stuur bericht naar 3
    // geselecteerden" van vorig jaar meenemen naar dit jaar.
    setSelectedIds(new Set())
    setExpandedId(null)
    setBroadcastRecipients(null)
    viewEditionRef.current = next
    setViewEdition(next)
  }

  async function refreshAdminState() {
    const res = await fetchAdminState(token)
    if (res.ok) setAdminState(res.data)
  }

  async function load() {
    // Bewust uit de ref en niet uit `viewEdition`: zie kiesEditie() hierboven.
    const edition = viewEditionRef.current
    const seq = ++loadSeq.current
    const query = edition === null ? '' : `?edition=${edition}`
    const regsRes = await api<{
      registrations: AdminRegistration[]; edition: number; isActiveEdition: boolean
    }>(`/api/admin/registrations${query}`, { headers: { Authorization: `Bearer ${token}` } })
    // Een 401 blijft wél gelden, ook als deze lading achterhaald is: de
    // sessie is dan verlopen en de nieuwere lading loopt tegen hetzelfde aan.
    if (!regsRes.ok && regsRes.status === 401) onLogout()
    // Achterhaald als er intussen een nieuwere lading startte, óf als de
    // keuze veranderde terwijl deze onderweg was — die tweede check is wat
    // een mutatie-verversing ervan weerhoudt de rijen van de verlaten editie
    // terug te zetten.
    if (seq !== loadSeq.current || edition !== viewEditionRef.current) return
    if (!regsRes.ok) {
      setError('Aanmelden mislukt of server fout.')
      return
    }
    setError(null)
    setRegistrations(regsRes.data.registrations)
    setShownEdition(regsRes.data.edition ?? null)
    setIsActiveEdition(regsRes.data.isActiveEdition !== false)
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, viewEdition])

  // De plattegrond hangt NIET aan de editie-keuze en ook niet aan mutaties:
  // hij levert enkel de polygonen (`allSpotIds` en de kaart in de handmatige
  // inschrijving). Bezetting komt hier uit `registrations`, niet uit de
  // `taken`-vlaggen van deze call. Zat hij in `load()`, dan kostte elke klik
  // op een jaartal — en elke betaald-vink — een tweede volledige
  // gepagineerde `listAll()` bovenop de gevraagde editie-lezing, want
  // /api/spots berekent die vlaggen wél.
  useEffect(() => {
    let alive = true
    api<{ meta: MapMeta; spots: PublicSpot[] }>('/api/spots').then((res) => {
      if (!alive) return
      if (res.ok) setSpots(res.data)
      else setError('Plekken konden niet geladen worden.')
    })
    return () => { alive = false }
  }, [token])

  useEffect(() => {
    refreshAdminState()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token])

  // Een sluiting/heropening raakt allebei: activeEditionYear() kan meteen
  // opschuiven (de tabel en de bezetting zijn daarop gescoopt) én de server
  // zet Fase 2 en de wachtlijst terug uit, wat de kaarten hieronder tonen.
  async function onSeasonChange() {
    await Promise.all([load(), refreshAdminState()])
  }

  // De lijst edities voor de kiezer komt uit /admin/state, bewust niet uit de
  // inschrijvingen: een jaar zonder inschrijvingen is nog altijd een echte
  // editie, en dat uitzoeken zou een volledige alle-edities-lezing kosten.
  const editions = adminState?.editions ?? []
  const activeEdition = adminState?.season?.activeEdition ?? null

  // Visible rows = search + optional "alleen zonder plek" / "alleen met plek"
  // filter. Only "alleen zonder plek" reorders to assignment-priority
  // (buurtbewoners first, then oldest); the everyday table keeps the
  // server's newest-first.
  const filtered = useMemo(() => {
    const regs = registrations ?? []
    const searched = filter
      ? regs.filter((r) =>
          [r.naam, r.email, r.gewenstAdres, r.spots.join(' '), r.zone ?? ''].some((v) =>
            v.toLowerCase().includes(filter.toLowerCase()),
          ),
        )
      : regs
    let result = searched
    if (onlyUnassigned) result = sortForAssignment(result.filter((r) => r.spots.length < r.aantalPlaatsen))
    if (onlyWithSpot) result = result.filter((r) => r.spots.length > 0)
    if (paidFilter === 'betaald') result = result.filter((r) => r.betaald)
    else if (paidFilter === 'onbetaald') result = result.filter((r) => !r.betaald)
    return result
  }, [registrations, filter, onlyUnassigned, onlyWithSpot, paidFilter])

  async function assign(pageId: string, spotId: string) {
    setBusy(pageId)
    const res = await api<{ freezeFailed: boolean }>(`/api/admin/registrations/${pageId}/spot`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ spotId }),
    })
    setBusy(null)
    if (!res.ok) {
      alert(res.error === 'taken' ? 'Plek is al bezet.' : foutTekst(res.error, 'Toewijzen mislukt.'))
    } else if (res.data.freezeFailed) {
      alert('Plek toegewezen, maar het plek-adres kon niet bevroren worden. Probeer de toewijzing opnieuw.')
    }
    load()
  }

  function toggleSelect(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAllSelect() {
    // Operate on the visible (filtered) rows only, so "select all" never
    // reaches rows hidden by the search or "alleen zonder plek" filter.
    const all = filtered.map((r) => r.id)
    const everySelected = all.length > 0 && all.every((id) => selectedIds.has(id))
    setSelectedIds(everySelected ? new Set() : new Set(all))
  }

  function toggleExpand(id: string) {
    setEditError(null)
    setExpandedId((cur) => (cur === id ? null : id))
  }

  async function togglePaid(pageId: string, betaald: boolean) {
    setBusy(pageId)
    const res = await api(`/api/admin/registrations/${pageId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ betaald }),
    })
    setBusy(null)
    if (!res.ok) alert(foutTekst(res.error, 'Kon Betaald niet bijwerken.'))
    load()
  }

  async function saveEdit(pageId: string, fields: Record<string, unknown>) {
    if (Object.keys(fields).length === 0) {
      setExpandedId(null)
      return
    }
    setBusy(pageId)
    setEditError(null)
    const res = await api(`/api/admin/registrations/${pageId}`, {
      method: 'PUT',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify(fields),
    })
    setBusy(null)
    if (!res.ok) {
      setEditError(res.errors ? Object.values(res.errors).join(' · ') : foutTekst(res.error, 'Opslaan mislukt.'))
      return
    }
    setExpandedId(null)
    load()
  }

  async function deleteReg(pageId: string) {
    setBusy(pageId)
    const res = await api(`/api/admin/registrations/${pageId}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` },
    })
    setBusy(null)
    if (!res.ok) {
      alert(res.error === 'season-closed'
        ? 'Dit seizoen is gesloten — inschrijvingen van deze editie zijn nu de "vorige editie"-archivering en kunnen niet meer verwijderd worden.'
        : foutTekst(res.error, 'Verwijderen mislukt.'))
      return
    }
    setExpandedId(null)
    setSelectedIds((prev) => {
      const next = new Set(prev)
      next.delete(pageId)
      return next
    })
    load()
  }

  function openBroadcastForOne(r: AdminRegistration) {
    setBroadcastRecipients([r])
  }
  function openBroadcastForSelected() {
    if (!registrations) return
    const recipients = registrations.filter((r) => selectedIds.has(r.id))
    if (recipients.length > 0) setBroadcastRecipients(recipients)
  }
  function onBroadcastSent(sent: number, failed: { naam: string; reason: string }[]) {
    setBroadcastRecipients(null)
    setSelectedIds(new Set())
    if (failed.length === 0) setToast(`${sent} mail${sent === 1 ? '' : 's'} verstuurd.`)
    else setToast(`${sent} verstuurd, ${failed.length} mislukt: ${failed.map((f) => f.naam).join(', ')}`)
    setTimeout(() => setToast(null), 6000)
  }

  // Bulk "je plek is X"-bevestiging naar iedereen met plek die nog geen mail kreeg.
  async function notifyAssigned() {
    if (!registrations) return
    const teVersturen = registrations.filter(
      (r) => r.spots.length > 0 && !r.wachtlijst && !r.plekMailVerstuurd,
    )
    const withEmail = teVersturen.filter((r) => r.email)
    if (teVersturen.length === 0) {
      setToast('Iedereen met een plek kreeg al een mail.')
      setTimeout(() => setToast(null), 5000)
      return
    }
    const zonderMail = teVersturen.length - withEmail.length
    const ok = window.confirm(
      `Plek-bevestiging (met adres) versturen naar ${withEmail.length} standhouder(s) die nog geen mail kregen?` +
      (zonderMail > 0 ? `\n\n${zonderMail} zonder e-mail worden overgeslagen.` : '') +
      `\n\nWie al een mail kreeg, wordt overgeslagen. Bij een gratis maillimiet kan je morgen gewoon opnieuw klikken voor de rest.`,
    )
    if (!ok) return
    setBusy('notify-assigned')
    const res = await api<{ sent: number; total: number; failed: { naam: string; reason: string }[] }>(
      '/api/admin/notify-assigned',
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    )
    setBusy(null)
    if (!res.ok) {
      if (res.status === 401) onLogout()
      setToast(foutTekst(res.error, 'Versturen mislukt.'))
      setTimeout(() => setToast(null), 6000)
      return
    }
    const { sent, failed } = res.data
    setToast(
      failed.length === 0
        ? `${sent} plek-bevestiging${sent === 1 ? '' : 'en'} verstuurd.`
        : `${sent} verstuurd, ${failed.length} mislukt: ${failed.map((f) => f.naam).join(', ')}`,
    )
    setTimeout(() => setToast(null), 8000)
    // Ververs zodat de "nog te mailen"-teller de zonet aangevinkte mensen weglaat.
    await load()
  }

  // Bulk "kies je plek"-uitnodiging naar wie nog geen (volledige) plek heeft.
  async function notifyInvite() {
    if (!registrations) return
    const teMailen = registrations.filter(
      (r) => !r.wachtlijst && r.spots.length < r.aantalPlaatsen && !r.uitnodigingVerstuurd,
    )
    const withEmail = teMailen.filter((r) => r.email)
    if (teMailen.length === 0) {
      setToast('Iedereen zonder (volledige) plek kreeg al een uitnodiging.')
      setTimeout(() => setToast(null), 5000)
      return
    }
    // Waarschuw als zelf-kiezen (Fase 2) nog uit staat — dan is de link
    // alleen-lezen. Bewust een VERSE lezing en niet de `adminState` die deze
    // pagina al heeft: die is van bij het laden, en een seizoen dat intussen
    // in een ander tabblad gesloten en heropend werd heeft Fase 2 onderweg
    // uitgezet (closeSeason() doet dat). Op de gecachete vlag zou de
    // waarschuwing dan wegvallen en vertrekt de uitnodiging met een
    // alleen-lezen link. Eén extra lezing op een expliciete, zeldzame klik —
    // wat elders gebundeld werd was de storm bij het mounten, niet dit.
    const verseState = await fetchAdminState(token)
    const fase2Uit = verseState.ok && !verseState.data.openSeatingActivatedAt
    // Lukt die lezing niet, dan zwijgen we niet: "ik weet het niet" is hier
    // iets anders dan "het staat aan", en stilzwijgend de waarschuwing laten
    // vallen is net het geval dat deze verse lezing moet afdekken.
    const fase2Onbekend = !verseState.ok
    const zonderMail = teMailen.length - withEmail.length
    const ok = window.confirm(
      `"Kies je plek"-uitnodiging versturen naar ${withEmail.length} standhouder(s) zonder (volledige) plek?` +
      (zonderMail > 0 ? `\n\n${zonderMail} zonder e-mail worden overgeslagen.` : '') +
      (fase2Uit
        ? `\n\n⚠️ LET OP: zelf-kiezen (Fase 2) staat nog UIT — de link is dan alleen-lezen en mensen kunnen nog niet kiezen. Zet eerst Fase 2 aan.`
        : fase2Onbekend
        ? `\n\n⚠️ We konden niet nakijken of zelf-kiezen (Fase 2) aan staat. Staat het uit, dan is de link alleen-lezen. Controleer dat eerst hierboven.`
        : '') +
      `\n\nWie al een uitnodiging kreeg, wordt overgeslagen. Bij een gratis maillimiet kan je morgen gewoon opnieuw klikken voor de rest.`,
    )
    if (!ok) return
    setBusy('notify-invite')
    const res = await api<{ sent: number; total: number; failed: { naam: string; reason: string }[] }>(
      '/api/admin/notify-invite',
      { method: 'POST', headers: { Authorization: `Bearer ${token}` } },
    )
    setBusy(null)
    if (!res.ok) {
      if (res.status === 401) onLogout()
      setToast(foutTekst(res.error, 'Versturen mislukt.'))
      setTimeout(() => setToast(null), 6000)
      return
    }
    const { sent, failed } = res.data
    setToast(
      failed.length === 0
        ? `${sent} uitnodiging${sent === 1 ? '' : 'en'} verstuurd.`
        : `${sent} verstuurd, ${failed.length} mislukt: ${failed.map((f) => f.naam).join(', ')}`,
    )
    setTimeout(() => setToast(null), 8000)
    await load()
  }

  if (error) {
    return (
      <div className="container-narrow py-10">
        <p className="text-terracottaDark">{error}</p>
        <BigButton variant="ghost" onClick={onLogout}>Opnieuw aanmelden</BigButton>
      </div>
    )
  }
  if (!registrations || !spots) return <div className="container-narrow py-10 text-muted">Laden…</div>

  const allSpotIds = spots.spots.map((s) => s.id)
  const taken = new Set<string>()
  for (const r of registrations) for (const id of r.spots) taken.add(id)
  const teMailenCount = registrations.filter(
    (r) => r.spots.length > 0 && !r.wachtlijst && !r.plekMailVerstuurd,
  ).length
  const teUitnodigenCount = registrations.filter(
    (r) => !r.wachtlijst && r.spots.length < r.aantalPlaatsen && !r.uitnodigingVerstuurd,
  ).length
  // Welke editie dit scherm bedoelt te tonen, meteen bij de klik — niet pas
  // als de (gepagineerde) Notion-lezing terug is.
  const toonEditie = viewEdition ?? activeEdition ?? shownEdition
  // De rijen in `registrations` horen nog bij de vorige keuze zolang
  // `shownEdition` niet mee is. Ze dan tonen is op z'n best verwarrend (een
  // archiefbanner boven de rijen van dit jaar) — dus tonen we ze niet.
  const wisseltEditie = toonEditie !== null && shownEdition !== null && toonEditie !== shownEdition
  // Alleen-lezen zodra er een jaartal gekozen is, zonder op het antwoord te
  // wachten: de kiezer biedt de lopende editie enkel aan als "Huidige
  // editie", dus élk gekozen jaartal is per definitie een afgesloten editie.
  // Wachten op `isActiveEdition` liet de bewerk- en mailknoppen van dit jaar
  // openstaan terwijl de kiezer al een archiefjaar aanwees. Is `activeEdition`
  // nog onbekend, dan valt dit de veilige kant op (alleen-lezen).
  const archief = !isActiveEdition || (viewEdition !== null && viewEdition !== activeEdition)
  // Buiten het seizoen blijft de zonet gesloten editie de actieve, dus
  // `archief` is dan false en deze balk staat er gewoon. Beide bulkmails
  // worden server-side geweigerd (423 `season-closed`), dus aanbieden zou
  // enkel een foutmelding opleveren.
  const seizoenOpen = adminState?.season.registrationOpen !== false
  return (
    <div className="mx-auto max-w-5xl px-5 py-8">
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <h1 className="font-display text-6xl tracking-display uppercase">
          Beheer{toonEditie !== null && <span className="text-3xl align-middle ml-3 text-muted">{toonEditie}</span>}
        </h1>
        <div className="flex items-center gap-3">
          <label className="text-sm flex items-center gap-2">
            <span className="font-display tracking-display uppercase text-xs">Editie</span>
            <select
              className="input w-auto px-2 py-1.5"
              value={viewEdition === null ? '' : String(viewEdition)}
              onChange={(e) => kiesEditie(e.target.value === '' ? null : Number(e.target.value))}
            >
              <option value="">
                Huidige editie{activeEdition !== null ? ` (${activeEdition})` : ''}
              </option>
              {/* De lopende editie staat al in de optie hierboven; ze twee keer
                  aanbieden leest als twee verschillende dingen. */}
              {editions.filter((y) => y !== activeEdition).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </label>
          <button onClick={onLogout} className="text-sm text-muted underline">Afmelden</button>
        </div>
      </div>

      {archief && (
        <div className="card p-4 shadow-block mb-6 bg-purple/20">
          <div className="font-display text-2xl uppercase tracking-display mb-1">
            Archief — editie {toonEditie}
          </div>
          <p className="text-sm">
            Je kijkt naar een <strong>afgesloten editie</strong>. De inschrijvingen hieronder zijn
            alleen-lezen: toewijzen, betaald aanvinken, bewerken, verwijderen en mails versturen kan
            enkel voor de lopende editie. De getoonde adressen zijn de <em>bevroren</em> adressen van
            toen, niet die van de plattegrond van vandaag. Seizoen en marktdatum hieronder blijven
            wél aanpasbaar — die gaan over de instellingen, niet over deze rijen.{' '}
            <button
              type="button"
              onClick={() => kiesEditie(null)}
              className="underline"
            >
              Terug naar de huidige editie
            </button>
          </p>
        </div>
      )}

      {/* De cijfers komen uit `registrations`, dus ook zij horen nog bij de
          vorige keuze zolang de nieuwe lading onderweg is. */}
      {!wisseltEditie && (
        <StatsPanel
          registrations={registrations}
          totalSpots={allSpotIds.length}
          taken={taken.size}
          archief={archief}
        />
      )}

      {/* Alles in deze balk werkt op de lopende editie: toewijzen en de
          afdrukbare kaart lezen de huidige plattegrond, en beide bulkmails
          schrijven. In archiefmodus zouden ze dus stilletjes over een ander
          jaar gaan dan de tabel eronder. */}
      {!archief && (
      <div className="mb-6 flex flex-wrap gap-3">
        <Link
          to="/beheer/toewijzen"
          className="inline-block px-5 py-3 bg-ink text-lime font-display tracking-display uppercase border-[3px] border-ink shadow-block hover:translate-y-[2px]"
        >
          🧭 Plekken toewijzen →
        </Link>
        <Link
          to="/beheer/plekken"
          className="inline-block px-5 py-3 bg-offwhite text-ink font-display tracking-display uppercase border-[3px] border-ink shadow-block hover:translate-y-[2px]"
        >
          🗺 Plekken beheren →
        </Link>
        <Link
          to="/beheer/kaart"
          className="inline-block px-5 py-3 bg-offwhite text-ink font-display tracking-display uppercase border-[3px] border-ink shadow-block hover:translate-y-[2px]"
        >
          🖨 Afdrukbare kaart →
        </Link>
        <button
          type="button"
          onClick={notifyAssigned}
          disabled={busy === 'notify-assigned' || teMailenCount === 0 || !seizoenOpen}
          className="inline-block px-5 py-3 bg-lime text-ink font-display tracking-display uppercase border-[3px] border-ink shadow-block hover:translate-y-[2px] disabled:opacity-40"
        >
          {busy === 'notify-assigned'
            ? 'Bezig…'
            : teMailenCount === 0
              ? '✉ Alle plek-mails verstuurd'
              : `✉ Plek-bevestiging naar ${teMailenCount} nog te mailen`}
        </button>
        <button
          type="button"
          onClick={notifyInvite}
          disabled={busy === 'notify-invite' || teUitnodigenCount === 0 || !seizoenOpen}
          className="inline-block px-5 py-3 bg-purple text-offwhite font-display tracking-display uppercase border-[3px] border-ink shadow-block hover:translate-y-[2px] disabled:opacity-40"
        >
          {busy === 'notify-invite'
            ? 'Bezig…'
            : teUitnodigenCount === 0
              ? '✉ Alle uitnodigingen verstuurd'
              : `✉ Kies-je-plek-uitnodiging naar ${teUitnodigenCount} nog te mailen`}
        </button>
        {!seizoenOpen && (
          <p className="w-full text-sm text-muted -mt-1">
            De inschrijvingen zijn gesloten — bulkmails over plekken kunnen pas weer als het seizoen
            heropent. De markt van deze editie is immers al geweest.
          </p>
        )}
      </div>
      )}

      <SeasonCard token={token} state={adminState} onSeasonChange={onSeasonChange} />

      <EventDateCard token={token} state={adminState} />

      {!archief && (
        <>
          <OpenSeatingCard token={token} state={adminState} onChanged={refreshAdminState} />

          <WaitlistCard token={token} state={adminState} />

          <ManualRegistrationCard token={token} spots={spots.spots} meta={spots.meta} onCreated={load} />
        </>
      )}

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <input
          type="search"
          placeholder="Zoeken op naam, e-mail, plek…"
          className="input flex-1 min-w-[12rem]"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={onlyUnassigned}
            onChange={(e) => setOnlyUnassigned(e.target.checked)}
          />
          alleen zonder plek
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <input
            type="checkbox"
            checked={onlyWithSpot}
            onChange={(e) => setOnlyWithSpot(e.target.checked)}
          />
          alleen met plek (standhouders)
        </label>
        <label className="flex items-center gap-1.5 text-sm">
          <span>Betaling:</span>
          <select
            className="input w-auto px-2 py-1.5"
            value={paidFilter}
            onChange={(e) => setPaidFilter(e.target.value as 'alle' | 'betaald' | 'onbetaald')}
          >
            <option value="alle">alle</option>
            <option value="onbetaald">niet betaald</option>
            <option value="betaald">betaald</option>
          </select>
        </label>
        {!archief && (
          <button
            type="button"
            onClick={openBroadcastForSelected}
            disabled={selectedIds.size === 0}
            className="px-4 py-2 bg-ink text-lime font-display tracking-display uppercase border-[3px] border-ink shadow-block-sm disabled:opacity-40"
          >
            ✉ Stuur bericht naar {selectedIds.size} geselecteerd{selectedIds.size === 1 ? 'e' : 'en'}
          </button>
        )}
      </div>

      {wisseltEditie ? (
        <div className="card p-5 shadow-block text-muted">
          Inschrijvingen van {toonEditie} laden…
        </div>
      ) : (
      <RegistrationsTable
        registrations={filtered}
        readOnly={archief}
        allSpotIds={allSpotIds}
        takenSpotIds={taken}
        selectedIds={selectedIds}
        expandedId={expandedId}
        busyId={busy}
        onToggleSelect={toggleSelect}
        onToggleAllSelect={toggleAllSelect}
        onAssignSpot={assign}
        onTogglePaid={togglePaid}
        onToggleExpand={toggleExpand}
        renderEditRow={(r) => (
          <RegistrationEditRow
            registration={r}
            busy={busy === r.id}
            error={expandedId === r.id ? editError : null}
            onSave={(fields) => saveEdit(r.id, fields)}
            onDelete={() => deleteReg(r.id)}
            onSendMessage={() => openBroadcastForOne(r)}
            onCancel={() => { setEditError(null); setExpandedId(null) }}
          />
        )}
      />
      )}

      {broadcastRecipients && (
        <BroadcastModal
          recipients={broadcastRecipients}
          token={token}
          onClose={() => setBroadcastRecipients(null)}
          onSent={onBroadcastSent}
        />
      )}
      {toast && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-ink text-lime px-5 py-3 border-[3px] border-ink shadow-block z-50 font-display tracking-display uppercase text-sm">
          {toast}
        </div>
      )}
    </div>
  )
}

// €5 per standje — spiegelt RM_PRICE_PER_PLACE_CENTS=500 en de Bedrag-formule
// in Notion (Aantal plaatsen × 5). Zelfde constante als op de inschrijfpagina.
const PRIJS_PER_PLAATS_EUR = 5

// At-a-glance overzicht bovenaan /beheer: bezetting, wie nog een plek/betaling
// mist, en hoeveel er al binnen is. Vervangt de oude één-regel-teller.
function StatsPanel({
  registrations,
  totalSpots,
  taken,
  archief = false,
}: {
  registrations: AdminRegistration[]
  totalSpots: number
  taken: number
  // Bezetting wordt geteld tegen de *huidige* plattegrond. Voor een
  // afgesloten editie zegt dat niets — die draaide op de kaart van toen, die
  // sindsdien opnieuw geïmporteerd kan zijn. Dan tonen we alleen wat op de
  // rijen zelf staat en dus altijd waar blijft.
  archief?: boolean
}) {
  const totalPlaatsen = registrations.reduce((n, r) => n + r.aantalPlaatsen, 0)
  const vrij = totalSpots - taken
  const zonderPlek = registrations.filter(
    (r) => !r.wachtlijst && r.spots.length < r.aantalPlaatsen,
  ).length
  const betaald = registrations.filter((r) => r.betaald)
  const geInd = betaald.reduce((n, r) => n + r.aantalPlaatsen * PRIJS_PER_PLAATS_EUR, 0)
  const onbetaald = registrations.filter(
    (r) => !r.betaald && !r.wachtlijst && r.spots.length > 0,
  )
  const openstaand = onbetaald.reduce((n, r) => n + r.aantalPlaatsen * PRIJS_PER_PLAATS_EUR, 0)
  const wachtlijst = registrations.filter((r) => r.wachtlijst).length
  const buurt = registrations.filter((r) => r.buurtbewoner).length
  const eur = (n: number) => `€${n.toLocaleString('nl-BE')}`

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
      <Stat label="Inschrijvingen" value={registrations.length} sub={`${totalPlaatsen} standjes`} />
      {archief ? (
        <Stat label="Plekken toegewezen" value={taken} sub="op de kaart van toen" />
      ) : (
        <>
          <Stat label="Plekken bezet" value={taken} sub={`van ${totalSpots}`} />
          <Stat label="Vrije plekken" value={vrij} />
          <Stat
            label="Zonder plek"
            value={zonderPlek}
            sub="nog toe te wijzen"
            highlight={zonderPlek > 0 ? 'lime' : undefined}
          />
        </>
      )}
      <Stat label="Betaald" value={betaald.length} sub={`${eur(geInd)} geïnd`} />
      <Stat
        label="Openstaand"
        value={eur(openstaand)}
        sub={`${onbetaald.length} onbetaald`}
        highlight={archief ? undefined : openstaand > 0 ? 'red' : undefined}
      />
      <Stat label="Wachtlijst" value={wachtlijst} />
      <Stat label="Buurtbewoners" value={buurt} />
    </div>
  )
}

function Stat({
  label,
  value,
  sub,
  highlight,
}: {
  label: string
  value: number | string
  sub?: string
  highlight?: 'lime' | 'red'
}) {
  const bg = highlight === 'lime' ? 'bg-lime' : highlight === 'red' ? 'bg-red/15' : 'bg-offwhite'
  return (
    <div className={`border-[3px] border-ink shadow-block-sm p-3 ${bg}`}>
      <div className="font-display text-4xl leading-none tracking-display">{value}</div>
      <div className="font-display tracking-display uppercase text-xs mt-1">{label}</div>
      {sub && <div className="text-xs text-muted mt-0.5">{sub}</div>}
    </div>
  )
}

function SeasonCard({ token, state, onSeasonChange }: {
  token: string
  state: AdminState | null
  onSeasonChange: () => Promise<void>
}) {
  const [season, setSeason] = useState<(Season & { persisted?: boolean }) | null>(null)
  const [feedbackCount, setFeedbackCount] = useState(0)
  const [totaal, setTotaal] = useState(0)
  const [countsError, setCountsError] = useState<string | null>(null)
  const [nogNietBevroren, setNogNietBevroren] = useState(0)
  const [bevriesEdities, setBevriesEdities] = useState<Array<{ edition: number; count: number }>>([])
  const [busy, setBusy] = useState(false)
  const [reopensAt, setReopensAt] = useState('')
  // Any year, not just the last-closed one — POST /admin/season/archive
  // happily (re-)freezes any edition at any time. The persistent
  // `nogNietBevroren` badge below covers the common case (the CURRENT
  // edition still has unfrozen rows), but an older edition — one where the
  // optional column didn't exist yet, or where some writes failed years ago
  // — has no such live counter and stays reachable by typing its year here.
  const [freezeYear, setFreezeYear] = useState('')
  // Whether the value in that field was typed by the organiser or suggested
  // by this page. Their own input is never overwritten by a reload; a
  // suggestion the app made is ours to correct. Cleared when they empty the
  // field, so emptying it asks for a fresh suggestion rather than locking in
  // a blank one.
  const freezeYearEdited = useRef(false)

  // The value in that field is owned either by the organiser (they typed it)
  // or by this page (it suggested one). The input below is the ONLY thing
  // that claims it for the organiser; every programmatic write goes through
  // here and hands ownership back. That matters most right after the retry
  // runs: a year that has been *used* is no longer an instruction, and if it
  // stays pinned, the next reload can't replace it once that edition comes
  // back clean — leaving the button aimed at a finished year while other
  // editions still have work.
  function suggestFreezeYear(value: string) {
    freezeYearEdited.current = false
    setFreezeYear(value)
  }

  // Draait telkens `state` vervangen wordt — Inner geeft bij elke verse
  // /admin/state-lezing een nieuw object door, dus dit is precies het oude
  // load()-moment, alleen zonder eigen request.
  useEffect(() => {
    if (!state) return
    setSeason(state.season ?? null)
    setTotaal(state.registrationCounts.total)
    setFeedbackCount(state.registrationCounts.feedbackOntvangen ?? 0)
    setNogNietBevroren(state.registrationCounts.nogNietBevroren ?? 0)
    const pending = state.registrationCounts.nogNietBevrorenEdities ?? []
    setBevriesEdities(pending)
    setCountsError(state.countsError ?? null)
    // A reopensAt already in the past (right after it auto-reopened on its
    // own) isn't a real default to resubmit — the server would reject it
    // anyway now, but don't even offer it: let the date field start empty so
    // closing without touching it computes a fresh one server-side.
    const reopens = state.season?.reopensAt ?? ''
    setReopensAt(reopens && Date.parse(reopens) > Date.now() ? reopens : '')
    // Prefer an edition that actually has work waiting over the last-closed
    // one: those differ in exactly the case the warning exists for (an older
    // edition still unfinished after a newer one closed), and defaulting to
    // the last-closed year there would send the organiser to re-sweep a
    // clean edition and report "0" while the real backlog sits untouched.
    const pendingYears = pending.map((e) => e.edition)
    setFreezeYear((prev) => {
      if (freezeYearEdited.current && prev.trim() !== '') return prev
      // A suggestion that no longer names an edition with work is worse than
      // no suggestion at all: pressing the button then sweeps a clean year
      // and reports "0 bevroren", which reads as "niks te doen" while the
      // real backlog sits in another edition. Two ways to get there, both
      // ordinary — closing a newer edition (setClosed() points the field at
      // that one) while an older one is still unfinished, and clearing the
      // first of several pending editions.
      if (pendingYears.length > 0 && !pendingYears.includes(Number(prev))) {
        return String(pendingYears[0])
      }
      return prev || String(state.season?.editionYear ?? '')
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state])

  async function setClosed(closed: boolean) {
    if (closed && !window.confirm(
      'Inschrijvingen sluiten? Nieuwe inschrijvingen zijn dan niet meer mogelijk, ' +
      'de persoonlijke pagina wordt de feedbackpagina, en de plek-adressen van deze ' +
      'editie worden bevroren.',
    )) return
    setBusy(true)
    const res = await api<{
      season: Season & { persisted: boolean }
      frozen: number; skipped: number; failed: number; unrecoverable: number
      error: string | null; drainTimedOut: boolean
    }>(
      '/api/admin/season',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify(closed ? { closed: true, reopensAt: reopensAt || undefined } : { closed: false }),
      },
    )
    setBusy(false)
    if (!res.ok) {
      alert(res.error === 'reopens-in-past'
        ? 'Die heropendatum ligt al in het verleden — kies een datum in de toekomst.'
        : 'Seizoen wijzigen mislukt.')
      return
    }
    const warnings = [
      res.data.season.persisted === false &&
        'Let op: dit is (nog) niet opgeslagen op de server — zonder een werkende STATE_FILE ' +
        'verdwijnt deze wijziging bij de volgende herstart. Zeg het tegen Simon’s ontwikkelaar.',
      closed && res.data.error &&
        `Plekadressen bevriezen lukte niet (${res.data.error}). Probeer straks "Plekadressen opnieuw bevriezen".`,
      closed && !res.data.error && res.data.failed > 0 &&
        `${res.data.failed} plek-adressen konden niet bevroren worden. Probeer straks "Plekadressen opnieuw bevriezen".`,
      closed && res.data.unrecoverable > 0 &&
        `${res.data.unrecoverable} plek-adressen konden niet bevroren worden omdat de plattegrond ondertussen ` +
        'is aangepast — dit lost een nieuwe poging niet op. Vul "Adres bij sluiten" voor deze inschrijvingen ' +
        'zo nodig handmatig aan in Notion (bij één plek volstaat het adres zelf, bv. Fluweelstraat 1; bij ' +
        'meerdere plekken een lijst zoals ["Fluweelstraat 1","Fluweelstraat 2"]).',
      closed && res.data.drainTimedOut &&
        'Een inschrijving die nog liep op het moment van sluiten was nog niet klaar — ' +
        'die plek is mogelijk niet meegenomen. Druk over een paar minuten nog eens op ' +
        '"Plekadressen opnieuw bevriezen" om zeker te zijn.',
    ].filter(Boolean)
    if (warnings.length) {
      alert(warnings.join('\n\n'))
    } else if (closed) {
      alert(`${res.data.frozen} plek-adressen bevroren (${res.data.skipped} hadden al een adres of geen plek nodig).`)
    }
    // The state-driven freezeYear update only ever fills an *empty* field —
    // right for "don't clobber a year Simon is mid-typing on ordinary reload", wrong
    // here: this close just (re)froze a specific, known edition, and a
    // previously-set freezeYear (e.g. an older retry year still sitting in
    // the field, or last close's year, from before *this* one just closed a
    // *different* edition on reopen-then-close) would otherwise keep the
    // retry button pointed at the wrong year. Force it to the edition this
    // request actually just closed.
    if (closed && res.data.season.editionYear !== null) {
      // The app is choosing this value, not the organiser — so it stays
      // correctable by the refresh below and by later ones. Without
      // clearing the flag, a year they happened to type earlier would keep
      // this programmatic value pinned even once it names a clean edition.
      suggestFreezeYear(String(res.data.season.editionYear))
    }
    // activeEditionYear() can advance the instant this transition lands
    // (most visibly on reopen), and the registrations table / occupancy /
    // sibling cards above are all scoped to that edition — without this
    // they'd keep showing the closed edition's data until a full page
    // refresh, letting the organiser edit or e-mail last year's registrants.
    // Dit ververst ook deze kaart zelf: `state` komt van Inner.
    await onSeasonChange()
  }

  // Safe to call for any PAST edition, at any time — freezeSpotAddresses()
  // is idempotent and never touches an already-frozen row. Unlike
  // "Inschrijvingen sluiten" this also works once the season has
  // auto-reopened, when re-closing would stop the new edition's own
  // registrations just to retry an older year's addresses. The one edition
  // it refuses is the currently active one while it's still open — freezing
  // that early would capture a premature snapshot (spots are still being
  // assigned) that the real close-time freeze could then never correct,
  // since it skips anything already frozen.
  async function freezeAddresses() {
    const year = Number(freezeYear)
    if (!Number.isInteger(year) || year < 2000 || year > 2100) {
      alert('Vul een geldig jaartal in.')
      return
    }
    setBusy(true)
    const res = await api<{
      frozen: number; skipped: number; failed: number; unrecoverable: number
      error: string | null; drainTimedOut: boolean
    }>(
      '/api/admin/season/archive',
      { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ editionYear: year }) },
    )
    setBusy(false)
    if (!res.ok) {
      alert(res.error === 'season-still-open'
        ? 'Dit is de huidige, nog open editie — plekadressen bevriezen kan pas nadat je inschrijvingen sluit.'
        : 'Plekadressen bevriezen mislukt.')
      return
    }
    const warnings = [
      res.data.error &&
        `Plekadressen bevriezen lukte niet (${res.data.error}). Probeer straks opnieuw.`,
      !res.data.error && res.data.failed > 0 &&
        `${res.data.failed} plek-adressen lukten nog steeds niet. Probeer straks opnieuw.`,
      res.data.unrecoverable > 0 &&
        `${res.data.unrecoverable} plek-adressen voor ${year} konden niet bevroren worden omdat de plattegrond ` +
        'ondertussen is aangepast — opnieuw proberen lost dit niet op. Vul "Adres bij sluiten" voor deze ' +
        'inschrijvingen zo nodig handmatig aan in Notion (bij één plek volstaat het adres zelf, bv. ' +
        'Fluweelstraat 1; bij meerdere plekken een lijst zoals ["Fluweelstraat 1","Fluweelstraat 2"]).',
      res.data.drainTimedOut &&
        'Een inschrijving die nog liep was nog niet klaar — die plek is mogelijk niet meegenomen. Druk over een paar minuten nog eens op deze knop.',
    ].filter(Boolean)
    if (warnings.length) {
      alert(warnings.join('\n\n'))
    } else {
      alert(`${res.data.frozen} plek-adressen bevroren voor ${year} (${res.data.skipped} hadden dat al of geen plek nodig).`)
    }
    // Keep the year on screen (it's what just ran) but hand ownership back,
    // so the refresh below may point the button at another still-pending
    // edition now that this one has been swept.
    suggestFreezeYear(String(year))
    await onSeasonChange()
  }

  if (!season) return null
  const gesloten = !season.registrationOpen

  return (
    <div className={`card p-5 shadow-block mb-6 ${gesloten ? 'bg-lime/30' : ''}`}>
      <div className="font-display tracking-display uppercase text-sm mb-1">Seizoen</div>
      <div className="font-display text-3xl uppercase tracking-display mb-2">
        {gesloten
          ? `Gesloten — inschrijven kan weer vanaf ${season.reopensLabel || 'later'}`
          : 'Open — mensen kunnen zich inschrijven'}
      </div>
      <p className="text-sm mb-3">
        Sluit het seizoen zodra de markt achter de rug is. Inschrijven gaat op slot tot de
        heropendatum, <strong>/mijn-plek</strong> wordt de bedankt- en feedbackpagina, en de
        plek-adressen van deze editie worden bevroren zodat iedereen via dezelfde link altijd
        het juiste adres van toen terugvindt, ook als de plattegrond later verandert.
      </p>

      {countsError && (
        <p className="text-sm text-red mb-3">
          Notion is momenteel niet bereikbaar — aantallen hieronder zijn onbekend, maar sluiten/
          heropenen werkt gewoon.
        </p>
      )}

      {/* Blijft staan zolang er werk is, in tegenstelling tot de eenmalige
          melding bij het sluiten zelf: mis je die (tab dicht, telefoon op
          slot), dan herinnert dit je er alsnog aan. Bewust ook zichtbaar bij
          een open seizoen — juist dán valt het op, want een half mislukte
          bevriezing van vorig jaar zie je pas als je in mei weer in /beheer
          kijkt. De server telt daarvoor élke gesloten editie mee, en nooit
          een editie die nog lóópt: tijdens het seizoen is "wel een plek, nog
          geen bevroren adres" gewoon de normale toestand van elke rij. */}
      {!countsError && nogNietBevroren > 0 && (
        <p className="text-sm text-red mb-3">
          <strong>{nogNietBevroren}</strong>{' '}
          {nogNietBevroren === 1 ? 'inschrijving heeft' : 'inschrijvingen hebben'} een plek maar nog
          geen bevroren adres
          {bevriesEdities.length > 0 && (
            <>
              {' '}(
              {bevriesEdities.map((e, i) => (
                <span key={e.edition}>
                  {i > 0 ? ', ' : ''}<strong>{e.edition}</strong>: {e.count}
                </span>
              ))}
              )
            </>
          )}
          . Druk hieronder op “Plekadressen (opnieuw) bevriezen”
          {bevriesEdities.length === 1
            ? ` voor ${bevriesEdities[0].edition}`
            : bevriesEdities.length > 1
            ? ', voor elk van die jaartallen apart'
            : ''}
          .
        </p>
      )}

      {gesloten ? (
        <>
          <p className="text-sm mb-3">
            {countsError
              ? <>Feedback binnen: <strong>onbekend</strong></>
              : <>Feedback binnen: <strong>{feedbackCount}</strong> van {totaal}</>}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <BigButton onClick={() => setClosed(false)} disabled={busy} variant="ghost">
              {busy ? 'Bezig…' : 'Inschrijvingen heropenen'}
            </BigButton>
          </div>
        </>
      ) : (
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm">
            <span className="block font-display tracking-display uppercase text-xs mb-1">
              Heropent op
            </span>
            <input
              type="date"
              className="input"
              value={reopensAt}
              onChange={(e) => setReopensAt(e.target.value)}
            />
          </label>
          <BigButton onClick={() => setClosed(true)} disabled={busy}>
            {busy ? 'Bezig…' : 'Inschrijvingen sluiten'}
          </BigButton>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mt-4 pt-4 border-t-2 border-ink/20">
        <label className="text-sm">
          <span className="block font-display tracking-display uppercase text-xs mb-1">
            Editie
          </span>
          <input
            type="number"
            className="input w-28"
            value={freezeYear}
            // Locked while the sweep runs, like the button beside it. That
            // sweep can take minutes on a full edition, and it finishes by
            // putting the year it just ran back in this field — so anything
            // typed meanwhile would be silently discarded. Refusing the
            // input is the honest version of that: the whole control reads
            // as busy instead of accepting work it will throw away.
            disabled={busy}
            onChange={(e) => {
              freezeYearEdited.current = e.target.value.trim() !== ''
              setFreezeYear(e.target.value)
            }}
          />
        </label>
        <button
          type="button"
          onClick={freezeAddresses}
          disabled={busy}
          className="text-sm text-muted underline hover:text-ink pb-2"
        >
          Plekadressen (opnieuw) bevriezen
        </button>
      </div>
    </div>
  )
}

// De marktdag per editie. Tot nu toe stond "zaterdag 12 september 2026" letterlijk
// in de teksten op /, /praktisch, /reglement, op /mijn-plek en in de mails — en
// die verspringt niet mee als het seizoen in mei vanzelf heropent. Dit is het
// ene veld waar al die zinnen uit lezen.
//
// Per editie, niet één "volgende datum": /mijn-plek en "vorige editie" blijven
// jaren later nog de oude editie tonen, en één gedeeld vakje zou die
// geschiedenis herschrijven zodra de datum van volgend jaar bekend is.
function EventDateCard({ token, state }: { token: string; state: AdminState | null }) {
  const [dates, setDates] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<number | null>(null)
  const [saved, setSaved] = useState<number | null>(null)

  const editions = state?.editions ?? []
  const active = state?.season.activeEdition ?? null

  useEffect(() => {
    if (!state) return
    const map: Record<string, string> = {}
    for (const e of state.eventDates ?? []) map[String(e.edition)] = e.date
    setDates(map)
  }, [state])

  async function save(edition: number) {
    setBusy(edition)
    setSaved(null)
    const res = await api<{ date: string | null; persisted: boolean }>('/api/admin/event-date', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      // Leeg veld === wissen; de server maakt er dan null van en alle teksten
      // vallen terug op een formulering zonder datum.
      body: JSON.stringify({ edition, date: dates[String(edition)] || null }),
    })
    setBusy(null)
    if (!res.ok) {
      alert(res.error === 'bad-date'
        ? 'Dat is geen geldige datum.'
        : res.error === 'date-year-mismatch'
        ? `De datum moet in ${edition} vallen — je vulde een ander jaartal in.`
        : foutTekst(res.error, 'Datum opslaan mislukt.'))
      return
    }
    if (res.data.persisted === false) {
      alert(
        'De datum staat goed voor deze draaiende server, maar kon niet opgeslagen worden — ' +
        'zonder een werkende STATE_FILE verdwijnt hij bij de volgende herstart. ' +
        'Zeg het tegen Simon\u2019s ontwikkelaar.',
      )
      return
    }
    setSaved(edition)
    setTimeout(() => setSaved((cur) => (cur === edition ? null : cur)), 4000)
  }

  // Elke editie die er is, plus elke editie waarvoor al een datum bekend is —
  // die laatste kan van vóór deze kolom zijn en mag niet uit beeld vallen.
  const jaren = [...new Set([...editions, ...Object.keys(dates).map(Number)])]
    .filter((y) => Number.isInteger(y))
    .sort((a, b) => b - a)
  if (jaren.length === 0) return null

  return (
    <div className="card p-5 shadow-block mb-6">
      <div className="font-display tracking-display uppercase text-sm mb-1">Datum van de markt</div>
      <div className="font-display text-3xl uppercase tracking-display mb-2">
        {active !== null && dates[String(active)]
          ? nlDatum(dates[String(active)])
          : 'Nog niet ingevuld'}
      </div>
      <p className="text-sm mb-3">
        Deze datum staat op de homepage, op <strong>/praktisch</strong> en <strong>/reglement</strong>,
        op de persoonlijke plek-pagina en in de bevestigingsmails. Vul je hem niet in, dan zeggen die
        teksten gewoon &ldquo;op de dag zelf&rdquo; in plaats van een verkeerde datum.
      </p>
      <div className="grid gap-2">
        {jaren.map((y) => (
          <div key={y} className="flex flex-wrap items-center gap-3">
            <span className="font-display tracking-display uppercase text-sm w-16">
              {y}
              {y === active && <span className="text-muted text-xs"> nu</span>}
            </span>
            <input
              type="date"
              className="input w-auto"
              // De server weigert een datum buiten dit jaar (400
              // `date-year-mismatch`) — een editie *is* een jaartal. Deze
              // grenzen laten de datumkiezer dat al tonen in plaats van
              // achteraf een foutmelding.
              min={`${y}-01-01`}
              max={`${y}-12-31`}
              value={dates[String(y)] ?? ''}
              disabled={busy === y}
              onChange={(e) => setDates((prev) => ({ ...prev, [String(y)]: e.target.value }))}
            />
            <button
              type="button"
              onClick={() => save(y)}
              disabled={busy === y}
              className="text-sm underline text-muted hover:text-ink"
            >
              {busy === y ? 'Bezig\u2026' : 'Opslaan'}
            </button>
            {saved === y && <span className="text-sm">\u2713 bewaard</span>}
            <span className="text-sm text-muted">{nlDatum(dates[String(y)] ?? '')}</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function OpenSeatingCard({ token, state: adminState, onChanged }: {
  token: string
  state: AdminState | null
  onChanged: () => Promise<void>
}) {
  const [state, setState] = useState<{
    activatedAt: string | null
    counts: { total: number; buurtbewoners: number; nogGeenPlek: number }
    // De server weigert deze toggle sowieso terwijl het seizoen dicht is
    // (closeSeason() zet 'm anders zelf al uit) — dit is enkel om de knop
    // hier al grijs te tonen in plaats van een verwarrende mislukte klik.
    registrationOpen: boolean
  } | null>(null)
  const [busy, setBusy] = useState(false)
  const [lastResult, setLastResult] = useState<{ sent: number; failed: string[] } | null>(null)

  useEffect(() => {
    if (!adminState) return
    setState({
      activatedAt: adminState.openSeatingActivatedAt,
      counts: adminState.registrationCounts,
      registrationOpen: adminState.season.registrationOpen,
    })
  }, [adminState])

  async function openSeating(sendInvites: boolean) {
    if (!state) return
    if (sendInvites) {
      if (!window.confirm(`Plek-keuze openen én de ingebouwde uitnodigingsmail sturen naar ${state.counts.nogGeenPlek} inschrijving(en) zonder plek?`)) return
    } else if (!isActive) {
      if (!window.confirm('Plek-keuze openen? Standhouders kunnen dan zelf een vrije plek kiezen of ruilen via hun link. Er worden GEEN mails verstuurd — die stuur je zelf via "Stuur bericht" (met de plek-link).')) return
    }
    setBusy(true)
    const res = await api<{ activatedAt: string; mailsSent: number; mailsFailed: string[] }>(
      '/api/admin/open-seating',
      { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: JSON.stringify({ sendInvites }) },
    )
    setBusy(false)
    if (res.ok) {
      if (sendInvites) setLastResult({ sent: res.data.mailsSent, failed: res.data.mailsFailed })
      await onChanged()
    } else {
      alert('Mislukt. Probeer opnieuw.')
    }
  }

  if (!state) return null
  const isActive = state.activatedAt !== null

  return (
    <div className="card p-5 shadow-block mb-6 bg-lime/40">
      <div className="font-display tracking-display uppercase text-sm mb-1">Plek-keuze (Fase 2)</div>
      <div className="font-display text-3xl uppercase tracking-display mb-2">
        {isActive
          ? `Open sinds ${new Date(state.activatedAt!).toLocaleString('nl-BE')}`
          : 'Nog dicht'}
      </div>
      <p className="text-sm mb-3">
        Als dit open staat, kunnen standhouders via hun link zelf een vrije plek kiezen of ruilen.
        {' '}{state.counts.nogGeenPlek} {state.counts.nogGeenPlek === 1 ? 'inschrijving wacht' : 'inschrijvingen wachten'} nog op een plek
        ({state.counts.buurtbewoners} buurtbewoners, {state.counts.total - state.counts.buurtbewoners} niet-buurt).
        {' '}Mails stuur je zelf via <strong>Stuur bericht</strong> (zet "persoonlijke plek-link" aan).
      </p>
      {!state.registrationOpen && (
        <p className="text-sm text-muted mb-2">
          Inschrijvingen zijn gesloten — dit kan pas weer aangepast worden zodra het seizoen heropent.
        </p>
      )}
      {!isActive ? (
        <BigButton onClick={() => openSeating(false)} disabled={busy || !state.registrationOpen}>
          {busy ? 'Bezig…' : 'Plek-keuze openen'}
        </BigButton>
      ) : (
        <p className="text-sm font-semibold text-ink mb-2">✓ Plek-keuze staat open — standhouders kunnen kiezen/ruilen.</p>
      )}
      <button
        type="button"
        onClick={() => openSeating(true)}
        disabled={busy || state.counts.nogGeenPlek === 0 || !state.registrationOpen}
        className="block mt-2 text-xs text-muted underline disabled:opacity-40"
      >
        (optioneel) stuur de ingebouwde uitnodigingsmail naar wie nog geen plek heeft
      </button>
      {lastResult && (
        <p className="text-sm text-muted mt-3">
          {lastResult.sent} mails verstuurd
          {lastResult.failed.length > 0 ? `, ${lastResult.failed.length} gefaald: ${lastResult.failed.join(', ')}` : ''}.
        </p>
      )}
    </div>
  )
}

// Geen onChanged: de POST-response is gezaghebbend voor de enige vlag die
// deze kaart toont, en niets buiten haar leest `waitlistActive`. Een verse
// /admin/state per klik zou dus een volledige gepagineerde Notion-lezing
// kosten voor niets. Bij de volgende echte verversing (seizoen) synct ze
// gewoon mee.
function WaitlistCard({ token, state }: { token: string; state: AdminState | null }) {
  const [active, setActive] = useState<boolean | null>(null)
  // Zie OpenSeatingCard hierboven: de server weigert deze toggle sowieso
  // terwijl het seizoen dicht is, dit is enkel om de knop hier al grijs te
  // tonen in plaats van een verwarrende mislukte klik.
  const [registrationOpen, setRegistrationOpen] = useState(true)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!state) return
    setActive(state.waitlistActive)
    setRegistrationOpen(state.season.registrationOpen)
  }, [state])

  async function toggle() {
    if (active === null) return
    const next = !active
    if (next && !window.confirm('Wachtlijst aanzetten? Nieuwe inschrijvingen krijgen dan géén plek meer, enkel een plek op de wachtlijst.')) return
    setBusy(true)
    const res = await api<{ waitlistActive: boolean }>('/api/admin/waitlist', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: JSON.stringify({ active: next }),
    })
    setBusy(false)
    if (res.ok) setActive(res.data.waitlistActive)
    else alert('Wachtlijst wijzigen mislukt.')
  }

  if (active === null) return null

  return (
    <div className={`card p-5 shadow-block mb-6 ${active ? 'bg-red/10' : ''}`}>
      <div className="font-display tracking-display uppercase text-sm mb-1">Wachtlijst</div>
      <div className="font-display text-3xl uppercase tracking-display mb-2">
        {active ? 'Aan — nieuwe inschrijvingen komen op de wachtlijst' : 'Uit — inschrijvingen krijgen gewoon een plek'}
      </div>
      <p className="text-sm mb-3">
        Zet dit aan zodra de markt vol is. Nieuwe inschrijvers kunnen zich nog inschrijven, maar zien
        duidelijk dat ze op de wachtlijst staan en hoeven nog niet te betalen. Bestaande inschrijvingen
        blijven ongemoeid.
      </p>
      {!registrationOpen && (
        <p className="text-sm text-muted mb-2">
          Inschrijvingen zijn gesloten — dit kan pas weer aangepast worden zodra het seizoen heropent.
        </p>
      )}
      <BigButton onClick={toggle} disabled={busy || !registrationOpen} variant={active ? 'ghost' : undefined}>
        {busy ? 'Bezig…' : active ? 'Wachtlijst uitzetten' : 'Wachtlijst aanzetten'}
      </BigButton>
    </div>
  )
}

function ManualRegistrationCard({ token, spots, meta, onCreated }: {
  token: string
  spots: PublicSpot[]
  meta: MapMeta
  onCreated: () => void
}) {
  const [open, setOpen] = useState(false)
  const [naam, setNaam] = useState('')
  const [email, setEmail] = useState('')
  const [adres, setAdres] = useState('')
  const [aantal, setAantal] = useState(1)
  const [opmerkingen, setOpmerkingen] = useState('')
  const [buurtbewoner, setBuurtbewoner] = useState(false)
  const [skipEmail, setSkipEmail] = useState(false)
  const [pickedSpots, setPickedSpots] = useState<string[]>([])
  const [busy, setBusy] = useState(false)

  function reset() {
    setNaam(''); setEmail(''); setAdres(''); setAantal(1); setOpmerkingen('')
    setBuurtbewoner(false); setSkipEmail(false); setPickedSpots([])
  }

  function toggleSpot(id: string) {
    if (pickedSpots.includes(id)) setPickedSpots(pickedSpots.filter((x) => x !== id))
    else if (pickedSpots.length < aantal) setPickedSpots([...pickedSpots, id])
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    const res = await api<{
      registration: { naam: string; token: string }
      assigned: string[]
      mailSent: boolean
      freezeFailed: number
    }>(
      '/api/admin/register',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          naam, email, gewenstAdres: adres, aantalPlaatsen: aantal, opmerkingen,
          buurtbewoner, skipEmail,
          spotIds: pickedSpots,
        }),
      },
    )
    setBusy(false)
    if (!res.ok) {
      // `map-not-trusted` is handled here rather than via foutTekst(): the
      // shared text tells the organiser to re-close the season or wait for
      // the next edition, and on THIS screen neither of those works. Only an
      // edition that has no snapshot yet can gain one by re-closing (see
      // captureMapFingerprintForEdition()), and waiting for the next edition
      // would tag this person to the wrong year. Registering without picking
      // a spot is the one path that succeeds right now, so it stays named.
      alert(res.error === 'taken'
        ? 'Een gekozen plek is al bezet.'
        : res.error === 'map-not-trusted'
        ? 'Inschrijving mislukt: de plattegrond is niet meer betrouwbaar voor deze (gesloten) editie '
          + '(mogelijk herdraaid na het sluiten van het seizoen). Schrijf in zonder plek te kiezen — '
          + 'de plek kan daarna met de hand in Notion gezet worden.'
        : foutTekst(res.error, 'Inschrijving mislukt.'))
      return
    }
    if (res.data.freezeFailed > 0) {
      alert(
        'Inschrijving gelukt, maar het plek-adres bevriezen lukte niet. ' +
        'Druk bij Seizoen op "Plekadressen opnieuw bevriezen" om het opnieuw te proberen.',
      )
    }
    reset()
    setOpen(false)
    onCreated()
  }

  if (!open) {
    return (
      <div className="mb-6">
        <BigButton onClick={() => setOpen(true)}>+ Nieuwe inschrijving toevoegen</BigButton>
      </div>
    )
  }

  return (
    <div className="card p-5 shadow-block mb-6">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="font-display text-2xl uppercase tracking-display">Nieuwe inschrijving</h2>
        <button onClick={() => { reset(); setOpen(false) }} className="text-sm text-muted underline">Annuleer</button>
      </div>
      <form onSubmit={submit} className="grid gap-3">
        <input className="input" placeholder="Naam" value={naam} onChange={(e) => setNaam(e.target.value)} required />
        <input className="input" placeholder="E-mail (optioneel)" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
        <input className="input" placeholder="Adres" value={adres} onChange={(e) => setAdres(e.target.value)} required />
        <select className="input" value={aantal} onChange={(e) => setAantal(Number(e.target.value))}>
          {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} plaats{n > 1 ? 'en' : ''}</option>)}
        </select>
        <textarea className="input min-h-[4rem]" placeholder="Opmerkingen" value={opmerkingen} onChange={(e) => setOpmerkingen(e.target.value)} />
        <label className="flex items-center gap-2">
          <input type="checkbox" checked={buurtbewoner} onChange={(e) => setBuurtbewoner(e.target.checked)} />
          <span>Buurtbewoner</span>
        </label>
        {email && (
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={skipEmail} onChange={(e) => setSkipEmail(e.target.checked)} />
            <span>Geen bevestigingsmail sturen</span>
          </label>
        )}

        <div className="border-t border-line pt-3">
          <p className="font-display tracking-display uppercase text-sm mb-2">
            Plek kiezen — {pickedSpots.length} / {aantal} geselecteerd
          </p>
          <MapPicker
            spots={spots}
            meta={meta}
            mode="multi"
            selectedIds={pickedSpots}
            onToggle={toggleSpot}
            height={320}
          />
          {pickedSpots.length > 0 && (
            <p className="text-sm mt-2">Gekozen: <strong>{pickedSpots.join(', ')}</strong></p>
          )}
        </div>

        <BigButton type="submit" disabled={busy}>
          {busy ? 'Bezig…' : 'Inschrijving opslaan'}
        </BigButton>
      </form>
    </div>
  )
}
